import type {
  ServiceAccountingCommentDto,
  ServiceAccountingOperationDto,
  ServiceAccountingSummaryDto,
} from '@shared/api/contracts';
import type {
  ServiceAccountingByAsset,
  ServiceAccountingComment,
  ServiceAccountingOperation,
  ServiceAccountingSummary,
  ServiceOpType,
} from '../model/types';
import { toSafeIsoString } from './format-date';
import { normalizeOperationDto } from './normalize';
import { parseApiOperation } from './parse-api-operation';
import { parseWalletKind } from './wallet';
import { resolveAssetRemainder } from '@shared/lib/format-ledger-amount';
import { getAssetBalanceKey } from '@shared/lib/asset-balance';

const authorInitials = (authorName: string): string =>
  authorName
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

const safeNumber = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const asDecimalString = (value: unknown, fallback = '0'): string => {
  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return fallback;
};

export const mapServiceAccountingComment = (
  comment: ServiceAccountingCommentDto,
): ServiceAccountingComment => ({
  id: comment.id || `comment-${comment.createdAt}`,
  authorName: comment.authorName,
  authorInitials: authorInitials(comment.authorName || '?'),
  text: comment.text,
  createdAt: toSafeIsoString(comment.createdAt, new Date().toISOString()),
});

export const mapServiceAccountingOperation = (
  operation: ServiceAccountingOperationDto,
): ServiceAccountingOperation => {
  const dto = normalizeOperationDto(operation);
  const fallbackDate = new Date().toISOString();

  const entries = dto.entries.map((entry, entryIndex) => {
    const remainder = resolveAssetRemainder({
      assetLabel: entry.assetLabel,
      assetBalanceAmount: entry.assetBalanceAmount ?? null,
      balanceAmount: entry.balanceAmount,
    });

    return {
      id: String(entry.id ?? `${dto.id}-entry-${entryIndex}`),
      sum: asDecimalString(entry.sum),
      balanceAfter: remainder.amount,
      comment: entry.comment,
      walletId: String(entry.walletId ?? entryIndex),
      walletName: entry.wallet?.name || '—',
      walletKind: parseWalletKind(entry.wallet?.kind),
      invoiceId: entry.invoiceId,
      createdAt: toSafeIsoString(entry.createdAt, fallbackDate),
      lineTag: entry.lineTag,
      assetLabel: entry.assetLabel ?? null,
      network: entry.network ?? null,
      assetBalanceAmount: entry.assetBalanceAmount ?? null,
    };
  });

  return {
    id: dto.id,
    type: (dto.type || 'adjustment') as ServiceOpType,
    displayAmount: asDecimalString(dto.displayAmount),
    invoiceId: dto.invoiceId,
    assetLabel: dto.assetLabel ?? null,
    network: dto.network ?? null,
    timeFrom: toSafeIsoString(dto.timeFrom, fallbackDate),
    timeTo: toSafeIsoString(dto.timeTo, toSafeIsoString(dto.timeFrom, fallbackDate)),
    relatedOperationIds: dto.relatedOperationIds,
    entries,
    walletNames: Array.from(new Set(entries.map((entry) => entry.walletName))),
    comments: Array.isArray(dto.comments) ? dto.comments.map(mapServiceAccountingComment) : [],
    commentsCount: safeNumber(dto.commentsCount, dto.comments?.length ?? 0),
  };
};

export const mapServiceAccountingSummary = (
  summary: ServiceAccountingSummaryDto | null | undefined,
): ServiceAccountingSummary => {
  const byAsset = Array.isArray(summary?.byAsset)
    ? summary.byAsset
        .filter(
          (row): row is ServiceAccountingByAsset =>
            typeof row?.assetCode === 'string' &&
            typeof row?.network === 'string' &&
            typeof row?.total === 'string',
        )
        .map((row) => ({
          assetCode: row.assetCode,
          network: row.network,
          total: row.total,
        }))
    : [];

  const unique = new Map(byAsset.map((row) => [getAssetBalanceKey(row.assetCode, row.network), row]));

  return {
    totalDeposits: safeNumber(summary?.totalDeposits),
    totalWithdrawals: safeNumber(summary?.totalWithdrawals),
    exchangeCount: safeNumber(summary?.exchangeCount),
    commissions: safeNumber(summary?.commissions),
    expenses: safeNumber(summary?.expenses),
    refunds: safeNumber(summary?.refunds),
    adjustmentCount: safeNumber(summary?.adjustmentCount),
    byAsset: Array.from(unique.values()),
  };
};

export const mapServiceAccountingsResponse = (
  response: { data?: unknown[]; summary?: ServiceAccountingSummaryDto } | null | undefined,
): { operations: ServiceAccountingOperation[]; summary: ServiceAccountingSummary } => {
  const rawItems = Array.isArray(response?.data) ? response.data : [];

  const operations = rawItems.flatMap((item, index) => {
    const dto = parseApiOperation(item, index);
    if (!dto) {
      return [];
    }

    try {
      return [mapServiceAccountingOperation(dto)];
    } catch {
      return [];
    }
  });

  return {
    operations,
    summary: mapServiceAccountingSummary(
      response && 'summary' in response ? response.summary : undefined,
    ),
  };
};
